import { type ReactNode } from 'react';
import { useCallback, useMemo, useState } from 'react';
import * as React from 'react';
import { useLocation } from '@reach/router';
import { motion } from 'framer-motion';
import { rgba } from 'polished';
import styled, { css, useTheme } from 'styled-components';

import useMediaQuery from '@/utils/useMediaQuery';

import { ArrowLeftIcon, ArrowTopRightIcon, PlayIcon } from '../../icons/Icons';
import {
  DEFAULT_OPACITY,
  DEFAULT_TRANSITION,
  HEX_DEMO_AUTOPLAY,
  HEX_DEMO_VIDEO,
  INTERNAL_LINKS,
  navigate,
  SIGNUP_LINK,
  TRACKING_LABELS
} from '../../utils';
import { Banner, Button, Heading } from '../design-system';
import { FloatyDarkButton } from '../design-system/FloatyDarkButton';

import { FullScreenVideo } from './FullScreenVideo';

type HeroSize = 'large' | 'medium';
type VideoSize = 'default' | 'compact';

const DEFAULT_WIDTH = '966px';
const COMPACT_WIDTH = '874px';

const Wrapper = styled.div<{
  $hasVideo?: string;
}>`
  display: flex;
  align-items: stretch;
  max-width: ${({ theme }) => theme.maxWidth.PAGE};
  width: 100%;
  gap: 70px;
  padding-bottom: ${({ $hasVideo }) => ($hasVideo ? '100px' : '0')};
  justify-content: center;
  position: relative;
  z-index: 1;
  padding-bottom: 50px;
  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    flex-direction: column;
    padding: 0 30px;
    gap: 40px;
  }
`;

const Info = styled(motion.div)<{
  $size?: HeroSize;
  $videoSize?: VideoSize;
}>`
  display: flex;
  flex: none;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
  padding-left: 40px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    width: 100%;
    padding: 0;
  }

  @media (min-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    ${({ $videoSize }) => $videoSize === 'default' && 'min-height: 578px'};
  }

  ${({ $size }) =>
    $size === 'medium' &&
    css`
      width: 475px;
    `}

  ${({ $size }) =>
    $size === 'large' &&
    css`
      width: 590px;
    `}
`;

const StyledHeading = styled(Heading)<{
  $size?: HeroSize;
}>`
  font-size: 84px;
  text-shadow: 2px 3px 2px ${({ theme }) => rgba(theme.colors.BLACK, 0.1)};

  span {
    display: block;

    @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
      display: inline;
    }
  }

  ${({ $size }) =>
    $size === 'medium' &&
    css`
      font-size: 66px;
    `}

  ${({ $size }) =>
    $size === 'large' &&
    css`
      font-size: 78px;
    `}
`;

const StyledSmallTitle = styled(Heading)`
  font-size: 14px;
  margin-bottom: 20px;
`;

const Description = styled(motion.div)`
  display: flex;
  flex-direction: column;
  gap: 15px;

  p {
    margin-bottom: 0;
  }
`;

const Actions = styled(motion.div)`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 20px;
  padding-top: 10px;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    gap: 15px;
    align-items: center;
  }
`;

const ActionsMain = styled.div`
  display: flex;
  gap: 20px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MOBILE}) {
    gap: 10px;
    flex-direction: column;
    align-self: stretch;
  }
`;

const Video = styled(motion.div)`
  display: flex;
  flex: 1 1 auto;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    height: auto;
  }
`;

const PulsingLines = styled.div<{ $videoSize?: VideoSize }>`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  opacity: 0.5;

  span {
    position: absolute;
    z-index: 1;
    border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};

    &:nth-child(1) {
      top: -12px;
      left: -12px;
      bottom: -12px;
      right: -12px;
    }

    &:nth-child(2) {
      top: -24px;
      left: -24px;
      bottom: -24px;
      right: -24px;
      opacity: 0.85;
    }

    &:nth-child(3) {
      top: -36px;
      left: -36px;
      bottom: -36px;
      right: -36px;
      opacity: 0.7;
    }

    &:nth-child(4) {
      top: -48px;
      left: -48px;
      bottom: -48px;
      right: -48px;
      opacity: 0.55;
    }

    &:nth-child(5) {
      top: -60px;
      left: -60px;
      bottom: -60px;
      right: -60px;
      opacity: 0.4;
    }

    &:nth-child(6) {
      top: -72px;
      left: -72px;
      bottom: -72px;
      right: -72px;
      opacity: 0.25;
    }

    &:nth-child(7) {
      top: -84px;
      left: -84px;
      bottom: -84px;
      right: -84px;
      opacity: 0.1;
    }
  }
`;

const VideoPlaceholder = styled.div<{
  $videoSize?: VideoSize;
}>`
  display: flex;
  width: ${({ $videoSize }) =>
    $videoSize === 'default' ? DEFAULT_WIDTH : COMPACT_WIDTH};
  flex: none;
  position: absolute;
  border: 1px solid ${({ theme }) => theme.borderColor.DEFAULT};
  transition:
    transform ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING},
    scale ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING};

  video {
    height: 100%;
    width: 100%;
    border-radius: 2px;
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    position: relative;
    height: auto;
    width: 100%;

    video {
      height: auto;
      width: 100%;
      border-radius: 2px 2px 0 0;
    }

    ${PulsingLines} {
      display: none;
    }
    img {
      width: 100%;
      border-radius: 2px 2px 0px 0px;
    }
  }
`;

const VideoInner = styled.div<{ $videoSize: VideoSize }>`
  display: flex;
  align-items: center;
  height: 100%;
  width: 100%;
  position: relative;
  flex: 1 1 auto;
  min-height: 0;

  &:hover {
    ${VideoPlaceholder} {
      transform: translateY(-2px);
      scale: 1.005;
    }
  }

  @media (min-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    ${({ $videoSize }) =>
      $videoSize === 'compact' && 'transform: translateX(100px)'};
  }

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    display: flex;
    justify-content: center;
    height: auto;
  }
`;

const ButtonWrapper = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  z-index: 4;
  top: 0;
  left: 0;
  right: auto;
  bottom: 0;
  width: calc(50vw + 55px);
  max-width: ${DEFAULT_WIDTH};
  cursor: pointer;

  @media (max-width: ${({ theme }) => theme.mediaQuery.MEDIUM}) {
    width: 100%;
  }
`;

const Icon = styled(ArrowLeftIcon)`
  color: ${({ theme }) => rgba(theme.colors.LIGHT_PINK, 0.75)};
  margin-right: 10px;
`;

const BreadCrumbs = styled.ul`
  margin: 0 0 20px 0;
  padfding: 0;
  display: flex;
  felx-direction: row;
  align-items: center;
  justify-content: left;
  list-style: none;
`;

const BreadCrumbsItems = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin: 0;
  span {
    color: ${({ theme }) => rgba(theme.colors.WHITE, 1)};
  }
  &:nth-child(odd) {
    &::after {
      content: '/';
      display: inline-block;
      margin: 0 10px;
      color: ${({ theme }) => rgba(theme.colors.LIGHT_PINK, 0.75)};
    }
  }
`;

const DemoTime = styled.span`
  opacity: 0.5;
`;

const ANIMATIONS = {
  Info: {
    initial: { x: 30, opacity: DEFAULT_OPACITY },
    transition: {
      ...DEFAULT_TRANSITION,
      duration: 1
    },
    viewport: { once: true },
    whileInView: { x: 0, opacity: 1 }
  },
  Heading: {
    initial: { opacity: DEFAULT_OPACITY, x: 20 },
    transition: {
      ...DEFAULT_TRANSITION,
      duration: 0.5
    },
    viewport: { once: true },
    whileInView: { opacity: 1, x: 0 }
  },
  Description: {
    initial: { opacity: DEFAULT_OPACITY, x: 20 },
    transition: {
      ...DEFAULT_TRANSITION,
      duration: 0.5,
      delay: 0.1
    },
    viewport: { once: true },
    whileInView: { opacity: 1, x: 0 }
  },
  Actions: {
    initial: { opacity: DEFAULT_OPACITY, x: 20 },
    transition: {
      ...DEFAULT_TRANSITION,
      duration: 0.5,
      delay: 0.15
    },
    viewport: { once: true },
    whileInView: { opacity: 1, x: 0 }
  },
  Video: {
    initial: { opacity: DEFAULT_OPACITY, x: -30 },
    transition: {
      ...DEFAULT_TRANSITION,
      duration: 1
    },
    viewport: { once: true },
    whileInView: { opacity: 1, x: 0 }
  },
  VideoButton: {
    initial: { opacity: DEFAULT_OPACITY },
    transition: {
      ...DEFAULT_TRANSITION,
      delay: 0.2
    },
    viewport: { once: true },
    whileInView: { opacity: 1 }
  },
  FullScreenVideo: {
    initial: { opacity: 0 },
    animate: { opacity: 1 },
    exit: { opacity: 0 },
    transition: { ...DEFAULT_TRANSITION }
  }
};

interface HeroToutProps {
  autoplayingVideoPath?: string;
  banner?: {
    text: ReactNode;
    to: string;
  };
  customActions?: JSX.Element;
  headerContent: ReactNode;
  headerSize?: HeroSize;
  infoSize?: HeroSize;
  publishedAppLink?: string;
  smallTitle?: string;
  subHeader: JSX.Element;
  useCaseHero?: boolean;
  video?: string;
  videoLength?: number;
  videoSize?: VideoSize;
}

export const HeroTout: React.ComponentType<HeroToutProps> = React.memo(
  ({
    infoSize = 'medium',
    banner,
    subHeader,
    smallTitle,
    headerContent,
    headerSize,
    customActions,
    publishedAppLink,
    autoplayingVideoPath = HEX_DEMO_AUTOPLAY,
    useCaseHero,
    video,
    videoLength,
    videoSize = 'default'
  }) => {
    const [isVideoOpen, setIsVideoOpen] = useState(false);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.mediaQueryString.MOBILE);
    const { search } = useLocation();

    const _handlePlayVideo = useCallback(() => {
      setIsVideoOpen(true);
    }, []);

    const _handleViewTemplate = useCallback(() => {
      publishedAppLink && navigate(publishedAppLink, search);
    }, [publishedAppLink, search]);

    const _handleCloseVideo = useCallback(() => {
      setIsVideoOpen(false);
    }, []);

    const actionSection = useMemo(
      () =>
        customActions ? (
          <ActionsMain>{customActions}</ActionsMain>
        ) : (
          <>
            <ActionsMain>
              <Button
                fill={isMobile}
                link={`${SIGNUP_LINK}?source=hero`}
                size="large"
                stylez="loud"
                text="Get started for free"
              />
              <Button
                fill={isMobile}
                link={INTERNAL_LINKS.ContactUs}
                size="large"
                text="Talk to us"
              />
            </ActionsMain>
          </>
        ),
      [customActions, isMobile]
    );

    return (
      <Wrapper $hasVideo={video}>
        <Info {...ANIMATIONS.Info} $size={infoSize} $videoSize={videoSize}>
          <motion.div {...ANIMATIONS.Heading}>
            {banner && (
              <BreadCrumbs>
                <BreadCrumbsItems>
                  <Banner to={`${INTERNAL_LINKS.Templates}`}>
                    <Icon />
                    <span>Templates</span>
                  </Banner>
                </BreadCrumbsItems>
                <BreadCrumbsItems>
                  <Banner to={banner.to}>
                    <span>{banner.text}</span>
                  </Banner>
                </BreadCrumbsItems>
              </BreadCrumbs>
            )}
            {smallTitle && (
              <StyledSmallTitle renderAs="h6">{smallTitle}</StyledSmallTitle>
            )}
            <StyledHeading $size={headerSize} renderAs="h1">
              {headerContent}
            </StyledHeading>
          </motion.div>
          <Description {...ANIMATIONS.Description}>{subHeader}</Description>
          <Actions {...ANIMATIONS.Actions}>{actionSection}</Actions>
        </Info>
        <Video {...ANIMATIONS.Video}>
          <VideoInner $videoSize={videoSize}>
            {useCaseHero && publishedAppLink ? (
              <ButtonWrapper
                {...ANIMATIONS.VideoButton}
                onClick={_handleViewTemplate}>
                <FloatyDarkButton
                  icon={<ArrowTopRightIcon />}
                  text="View template"
                />
              </ButtonWrapper>
            ) : (
              <ButtonWrapper
                {...ANIMATIONS.VideoButton}
                onClick={_handlePlayVideo}>
                <FloatyDarkButton
                  detail={
                    <DemoTime>
                      ({videoLength ? `${videoLength} min` : '5 min'})
                    </DemoTime>
                  }
                  icon={<PlayIcon />}
                  text="Watch demo"
                />
              </ButtonWrapper>
            )}
            <VideoPlaceholder $videoSize={videoSize}>
              <video
                autoPlay={true}
                controls={false}
                loop={true}
                muted={true}
                playsInline={true}
                poster="/images/home-poster.png">
                <source src={autoplayingVideoPath} type="video/mp4" />
              </video>
              <PulsingLines $videoSize={videoSize}>
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
                <span />
              </PulsingLines>
            </VideoPlaceholder>
          </VideoInner>
        </Video>
        <FullScreenVideo
          isOpen={isVideoOpen}
          path={video || HEX_DEMO_VIDEO}
          trackingLabel={TRACKING_LABELS.VideoPlayed}
          onClose={_handleCloseVideo}
        />
      </Wrapper>
    );
  }
);
